<template>
  <div class="p-4 pt-2">
    <!-- Info banner -->
    <v-alert color="blue" type="info" outlined dense class="text-sm font-medium mb-4">
      These settings mirror the Personal Loan General Settings. Adjust them to configure this
      sub-case's loan behaviour.
    </v-alert>

    <v-row>
      <!-- ───────────────── Column 1 – Dropdowns ───────────────── -->
      <v-col cols="12" md="6">
        <v-card outlined class="pa-4 rounded-lg mb-4">
          <div class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">
            Loan Configuration
          </div>

          <!-- Accounting Type -->
          <v-select
            v-model="settings.accountingType"
            :items="accountingTypeOptions"
            label="Accounting Type"
            outlined
            dense
            class="mb-3"
            clearable
            prepend-inner-icon="mdi-calculator-variant"
          />

          <!-- Loan Number Series -->
          <v-select
            v-model="settings.autoAgreementType"
            :items="loanNumberSeriesOptions"
            label="Loan Number Series"
            outlined
            dense
            class="mb-3"
            clearable
            prepend-inner-icon="mdi-numeric"
          />

          <!-- Grace Preference -->
          <v-text-field
            v-model.number="settings.gracePreference"
            label="Grace Preference (Days)"
            type="number"
            :min="0"
            :max="10000"
            outlined
            dense
            class="mb-3"
            prepend-inner-icon="mdi-calendar-clock"
            hint="Number of grace days allowed after EMI due date"
            persistent-hint
          />

          <!-- SOA Type -->
          <v-select
            v-model="settings.soaType"
            :items="soaTypeOptions"
            label="SOA Type"
            outlined
            dense
            class="mb-3"
            clearable
            prepend-inner-icon="mdi-file-document-outline"
          />

          <!-- Ledger Name Type -->
          <v-select
            v-model="settings.ledgerNameType"
            :items="ledgerNameTypeOptions"
            label="Ledger Name Type"
            outlined
            dense
            class="mb-3"
            clearable
            prepend-inner-icon="mdi-tag-text-outline"
          />

          <!-- CAM Sanction Number Type -->
          <v-select
            v-model="settings.sanctionNumberType"
            :items="sanctionNumberTypeOptions"
            label="CAM Sanction Number Type"
            outlined
            dense
            class="mb-3"
            clearable
            prepend-inner-icon="mdi-barcode"
          />

          <!-- Auto Generated File No -->
          <v-select
            v-model="settings.autoGeneratedFileNo"
            :items="autoGeneratedFileNoOptions"
            label="Auto Generated File No"
            outlined
            dense
            class="mb-3"
            clearable
            prepend-inner-icon="mdi-file-cog-outline"
          />

          <!-- Co-Borrower and Guarantor Limit -->
          <v-select
            v-model="settings.coBorrowerGuarantorLimit"
            :items="coBorrowerGuarantorLimitOptions"
            label="Co-Borrower and Guarantor Limit"
            outlined
            dense
            class="mb-3"
            clearable
            prepend-inner-icon="mdi-account-group-outline"
            hint="System will validate the specified condition during submission"
            persistent-hint
          />

          <!-- NOC Number Type -->
          <v-select
            v-model="settings.nocNumberType"
            :items="nocNumberTypeOptions"
            label="NOC Number Type"
            outlined
            dense
            class="mb-3"
            clearable
            prepend-inner-icon="mdi-certificate-outline"
          />

          <!-- Property Collateral Page -->
          <v-select
            v-model="settings.propertyCollateralPage"
            :items="[
              { text: 'Old', value: 0 },
              { text: 'New', value: 1 }
            ]"
            label="Property Collateral Page"
            outlined
            dense
            class="mb-3"
            clearable
            prepend-inner-icon="mdi-home-edit-outline"
            hint="Selects which version of the Property Collateral page to use"
            persistent-hint
          />

          <!-- Ignore Loan Receivable Amount Check -->
          <v-select
            v-model="settings.isIgnoreLoanReceiveableAmountCheck"
            :items="[
              { text: 'No', value: 0 },
              { text: 'Yes', value: 1 }
            ]"
            label="Ignore Loan Receiveable Amount Check"
            outlined
            dense
            class="mb-3"
            clearable
            prepend-inner-icon="mdi-cash-remove"
            hint="When Yes, excess collected amount will be parked automatically"
            persistent-hint
          />

          <!-- ForeClosure Letter Generation Validity -->
          <v-text-field
            v-model.number="settings.fclGenerationValidity"
            label="ForeClosure Letter Generation Validity (Days)"
            type="number"
            :min="0"
            outlined
            dense
            class="mb-3"
            prepend-inner-icon="mdi-calendar-check-outline"
            clearable
          />

          <!-- Show Insurance -->
          <v-select
            v-model="settings.showInsurance"
            :items="showInsuranceOptions"
            label="Show Insurance"
            outlined
            dense
            class="mb-3"
            clearable
            prepend-inner-icon="mdi-shield-check-outline"
          />

          <!-- Cash Disbursal Amount Limit (shown when cashDisbursalLimit is enabled) -->
          <v-text-field
            v-if="settings.isCashDisbursalLimit"
            v-model.number="settings.cashDisbursalAmountLimit"
            label="Cash Disbursal Amount Limit (₹)"
            type="number"
            :min="0"
            outlined
            dense
            class="mb-3"
            prepend-inner-icon="mdi-cash"
            hint="If sum of Disbursement amount > this limit, system will throw a validation error"
            persistent-hint
          />
        </v-card>

        <!-- Lead Source & Loan Type -->
        <v-card outlined class="pa-4 rounded-lg mb-4">
          <div class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">
            Lead &amp; Loan Classification
          </div>

          <!-- Lead Source -->
          <v-autocomplete
            v-model="settings.leadSourceIds"
            :items="leadSourceOptions"
            item-text="text"
            item-value="value"
            label="Lead Source"
            multiple
            chips
            small-chips
            outlined
            dense
            class="mb-3"
            clearable
            prepend-inner-icon="mdi-account-arrow-right-outline"
          />

          <!-- Loan Type -->
          <v-autocomplete
            v-model="settings.loanTypeIds"
            :items="loanTypeOptions"
            item-text="text"
            item-value="value"
            label="Loan Type"
            multiple
            chips
            small-chips
            outlined
            dense
            clearable
            prepend-inner-icon="mdi-bank-outline"
          />
        </v-card>
      </v-col>

      <!-- ───────────────── Column 2 – Feature Toggles ───────────────── -->
      <v-col cols="12" md="6">
        <v-card outlined class="pa-4 rounded-lg mb-4">
          <div class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">
            Feature Toggles
          </div>

          <div>
            <div
              v-for="toggle in toggleFields"
              :key="toggle.key"
              class="flex items-center justify-between py-2 border-b border-gray-100"
            >
              <!-- Label + info icon -->
              <div class="flex items-center flex-1 pr-4">
                <span class="text-sm font-medium text-gray-700">{{ toggle.label }}</span>
                <v-tooltip v-if="toggle.info" top max-width="280">
                  <template #activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" x-small color="blue darken-1" class="ml-1">
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <span>{{ toggle.info }}</span>
                </v-tooltip>
              </div>

              <!-- Switch -->
              <v-switch
                :input-value="settings[toggle.key]"
                @change="onToggle(toggle.key, $event)"
                color="primary"
                dense
                hide-details
                class="mt-0 pt-0 flex-shrink-0"
              />
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
export default {
  name: 'GeneralSettings',

  props: {
    value: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      syncingFromProp: false,
      settings: this.buildDefaults(this.value),

      // ── Toggle field definitions ──────────────────────────────────────────
      toggleFields: [
        {
          key: 'officialReceipt',
          label: 'Print Official Receipts',
          info: 'Enable to print official receipts on payment'
        },
        { key: 'showAdvanceEMIOptions', label: 'Show Advance EMI Options', info: '' },
        { key: 'showAssetTab', label: 'Show Asset Tab', info: '' },
        {
          key: 'enableLPCOptions',
          label: 'Enable LPC Options',
          info: 'Enables Late Payment Charge options'
        },
        { key: 'smsForECSPDC', label: 'SMS For ECS / PDC', info: '' },
        {
          key: 'lpcOnPrintReceipt',
          label: 'Show LPC On Print',
          info: 'Show Late Payment Charges on printed receipts'
        },
        { key: 'sendWelcomeSMS', label: 'Send Welcome SMS', info: '' },
        {
          key: 'autoAdjustLPC',
          label: 'Auto Adjust LPC',
          info: 'Automatically adjust Late Payment Charges'
        },
        {
          key: 'otherLoanSegment',
          label: 'Loan Segment',
          info: 'Enable Other Loan Segment classification'
        },
        { key: 'showIndicativeROI', label: 'Show Indicative ROI', info: '' },
        {
          key: 'autoFinanceCreation',
          label: 'Auto Finance Creation (API)',
          info: 'Enable automatic loan creation via API'
        },
        {
          key: 'isPaymentAutoPosting',
          label: 'Is Payment Auto Posting',
          info: 'Automatically post payment entries to accounting'
        },
        { key: 'restrictPartialReceipt', label: 'Restrict Partial Receipt', info: '' },
        { key: 'allowDuplicateLead', label: 'Allow Duplicate Lead', info: '' },
        { key: 'allowDuplicateLoan', label: 'Allow Multiple Loans', info: '' },
        { key: 'initiatedAsCE', label: 'Initiated As CE', info: '' },
        { key: 'allowPrimaryLoanClosure', label: 'Allow Primary Loan Closure', info: '' },
        { key: 'enableDealer', label: 'Enable Dealer', info: '' },
        {
          key: 'autoFinanceSettlement',
          label: 'Loan Auto Closure',
          info: 'Automatically close the loan when all dues are cleared'
        },
        {
          key: 'pgCollectionAmountType',
          label: 'PG Collection Amount Type',
          info: 'If enabled, all dues will be shown. Combined with Auto Adjusted EMI: shows sum of VAS, Handloan Charge, LPI, EMI Dues'
        },
        { key: 'isPGEditAmount', label: 'PG Edit Amount', info: '' },
        { key: 'aggrementNoChecker', label: 'Duplicate Agreement No Checker', info: '' },
        { key: 'showRSP', label: 'Show Co-Lending', info: '' },
        { key: 'isSettlementwithLoanswap', label: 'Is Settlement With Loanswap', info: '' },
        {
          key: 'promoterNameInCIBILSubReport',
          label: 'Show Promoter Name',
          info: 'If enabled, Promoter name will appear in CIBIL Submission report for non-individual contacts'
        },
        {
          key: 'sendACHSMSforPresentment',
          label: 'Send ACH/PDC SMS for Presentment',
          info: 'If enabled, triggers ACH/PDC Posting and ACH/PDC Clearance SMS instead of Accept Installment'
        },
        {
          key: 'isCashDisbursalLimit',
          label: 'Cash Disbursal Limit',
          info: 'Mode of Payment cash is restricted based on cash disbursal amount'
        },
        {
          key: 'allowLeadWithSecurityDetails',
          label: 'Allow Lead With Security Details',
          info: 'If enabled, minimum 1 security must be added for secured loans'
        },
        {
          key: 'isWorkFlowRequiredForFinanceUpload',
          label: 'Is WorkFlow Required For Finance Upload',
          info: 'If enabled, finance creation workflow is triggered after Excel uploads'
        },
        {
          key: 'isSchemeLTV',
          label: 'Scheme LTV',
          info: 'If enabled, LTV restriction defined in the scheme will apply during loan creation'
        },
        {
          key: 'isEnableExcessAmtParked',
          label: 'Auto-Park Excess Collected Amount',
          info: 'When enabled, excess collected amounts are automatically parked under the Excess Amount ledger'
        },
        { key: 'isGenerateFCL', label: 'ForeClosure Letter Generation Mandatory', info: '' }
      ],

      // ── Dropdown option lists ─────────────────────────────────────────────
      accountingTypeOptions: [
        { text: 'Accrual', value: 'HPHypothecation' },
        { text: 'Cash', value: 'Cash' }
      ],
      loanNumberSeriesOptions: [
        { text: 'Manual', value: 'Manual' },
        { text: 'Auto', value: 'Auto' }
      ],
      soaTypeOptions: [
        { text: 'Default SOA', value: 17 },
        { text: 'SOA Type1', value: 0 },
        { text: 'SOA Type2', value: 1 },
        { text: 'SOA Type3', value: 2 },
        { text: 'SOA Type4', value: 3 },
        { text: 'SOA Type5', value: 4 },
        { text: 'SOA Type6', value: 5 },
        { text: 'SOA Type7', value: 6 },
        { text: 'SOA Type8', value: 7 },
        { text: 'SOA Type 9', value: 8 },
        { text: 'SOA Type10', value: 9 },
        { text: 'SOA Type13', value: 12 },
        { text: 'SOA Type14', value: 14 },
        { text: 'SOA Type15', value: 16 },
        { text: 'SOA Type16', value: 15 },
        { text: 'SOA Type18', value: 18 },
        { text: 'SOA Type20', value: 20 },
        { text: 'SOA Type21', value: 21 },
        { text: 'SOA Type22', value: 22 }
      ],
      ledgerNameTypeOptions: [
        { text: 'RegNo_FName_LName_AgmtNum', value: 'RegNo_FName_LName_AgmtNum' },
        { text: 'FName_LName_AgmtNum', value: 'FName_LName_AgmtNum' },
        { text: 'AgmtNum_FName_LName', value: 'AgmtNum_FName_LName' }
      ],
      sanctionNumberTypeOptions: [
        { text: 'Default SanctionNumber', value: 0 },
        { text: 'ProductCode_CurrentYear_SanctionNo', value: 1 },
        { text: 'Manual', value: 2 }
      ],
      autoGeneratedFileNoOptions: [
        { text: 'Manual', value: 0 },
        { text: 'Agreement_No_As_A_File_Number', value: 1 },
        { text: 'Finance_Id_As_A_File_Number', value: 2 }
      ],
      coBorrowerGuarantorLimitOptions: [
        { text: 'Select', value: 0 },
        { text: '1 Co-Borrower', value: 1 },
        { text: '1 Guarantor', value: 2 },
        { text: '2 Co-Borrower', value: 3 },
        { text: '2 Guarantor', value: 4 },
        { text: '1 Co-Borrower OR 1 Guarantor', value: 5 },
        { text: '1 Co-Borrower AND 1 Guarantor', value: 6 },
        { text: '2 Co-Borrower OR 1 Guarantor', value: 7 },
        { text: '2 Co-Borrower AND 1 Guarantor', value: 8 }
      ],
      nocNumberTypeOptions: [
        { text: 'CompanyName_NOC_year_UniqueSeries', value: 0 },
        { text: 'LoanNo_NOC_year_UniqueSeries', value: 1 },
        { text: 'LoanNo_ProductCode_NOC_year', value: 2 },
        { text: 'LoanNo_ProductCode_NOC_year_UniqueSeries', value: 3 }
      ],
      showInsuranceOptions: [
        { text: 'Insurance', value: 0 },
        { text: 'Caution Deposit', value: 1 },
        { text: 'None', value: 2 }
      ],
      leadSourceOptions: [
        { text: 'DSA', value: 0 },
        { text: 'Consultant', value: 1 },
        { text: 'Dealer', value: 2 },
        { text: 'Customer', value: 3 },
        { text: 'DST', value: 4 },
        { text: 'Website', value: 5 },
        { text: 'Referral', value: 7 },
        { text: 'Tele Calling', value: 8 },
        { text: 'Others', value: 9 }
      ],
      loanTypeOptions: [
        { text: 'UnSecured Loan', value: 0 },
        { text: 'Top-Up Loan', value: 3 },
        { text: 'Secured Loan', value: 5 },
        { text: 'Green Channel', value: 6 },
        { text: 'Direct Channel', value: 7 },
        { text: 'Unsecured Part', value: 10 },
        { text: 'Mutual Fund(Secured)', value: 11 },
        { text: 'Invoice Discounting', value: 15 },
        { text: 'Credit Line', value: 16 },
        { text: 'Consumer Durable', value: 26 },
        { text: 'Insurance (Secured)', value: 27 },
        { text: 'Solar Loan', value: 28 },
        { text: 'Salaried Credit', value: 35 }
      ]
    }
  },

  watch: {
    // Only sync from parent when a fresh external value is set (e.g. loaded from DB).
    // Guard with syncingFromProp flag to prevent the round-trip loop:
    //   settings change → emit input → parent updates value → watcher → rebuild settings → loop
    value: {
      deep: true,
      handler(val) {
        if (this.syncingFromProp) return
        this.syncingFromProp = true
        this.settings = this.buildDefaults(val)
        this.$nextTick(() => {
          this.syncingFromProp = false
        })
      }
    },

    settings: {
      deep: true,
      handler(val) {
        if (this.syncingFromProp) return
        this.$emit('input', { ...val })
      }
    }
  },

  methods: {
    /** Toggle handler – directly mutates the reactive key without rebuilding the whole object */
    onToggle(key, val) {
      this.$set(this.settings, key, val)
    },

    buildDefaults(src) {
      const s = src || {}
      return {
        accountingType: s.accountingType !== undefined ? s.accountingType : 'HPHypothecation',
        autoAgreementType: s.autoAgreementType !== undefined ? s.autoAgreementType : 'Manual',
        gracePreference: s.gracePreference !== undefined ? s.gracePreference : 15,
        showInsurance: s.showInsurance !== undefined ? s.showInsurance : 2,
        ledgerNameType:
          s.ledgerNameType !== undefined ? s.ledgerNameType : 'RegNo_FName_LName_AgmtNum',
        officialReceipt: s.officialReceipt !== undefined ? s.officialReceipt : false,
        showAdvanceEMIOptions:
          s.showAdvanceEMIOptions !== undefined ? s.showAdvanceEMIOptions : false,
        showAssetTab: s.showAssetTab !== undefined ? s.showAssetTab : false,
        enableLPCOptions: s.enableLPCOptions !== undefined ? s.enableLPCOptions : false,
        smsForECSPDC: s.smsForECSPDC !== undefined ? s.smsForECSPDC : false,
        lpcOnPrintReceipt: s.lpcOnPrintReceipt !== undefined ? s.lpcOnPrintReceipt : false,
        sendWelcomeSMS: s.sendWelcomeSMS !== undefined ? s.sendWelcomeSMS : false,
        autoAdjustLPC: s.autoAdjustLPC !== undefined ? s.autoAdjustLPC : false,
        otherLoanSegment: s.otherLoanSegment !== undefined ? s.otherLoanSegment : false,
        soaType: s.soaType !== undefined ? s.soaType : 2,
        sanctionNumberType: s.sanctionNumberType !== undefined ? s.sanctionNumberType : 0,
        leadSourceIds: s.leadSourceIds !== undefined ? s.leadSourceIds : [],
        loanTypeIds: s.loanTypeIds !== undefined ? s.loanTypeIds : [],
        showIndicativeROI: s.showIndicativeROI !== undefined ? s.showIndicativeROI : false,
        autoFinanceCreation: s.autoFinanceCreation !== undefined ? s.autoFinanceCreation : false,
        autoGeneratedFileNo: s.autoGeneratedFileNo !== undefined ? s.autoGeneratedFileNo : 2,
        isPaymentAutoPosting: s.isPaymentAutoPosting !== undefined ? s.isPaymentAutoPosting : false,
        restrictPartialReceipt:
          s.restrictPartialReceipt !== undefined ? s.restrictPartialReceipt : false,
        allowDuplicateLead: s.allowDuplicateLead !== undefined ? s.allowDuplicateLead : false,
        allowDuplicateLoan: s.allowDuplicateLoan !== undefined ? s.allowDuplicateLoan : false,
        initiatedAsCE: s.initiatedAsCE !== undefined ? s.initiatedAsCE : false,
        allowPrimaryLoanClosure:
          s.allowPrimaryLoanClosure !== undefined ? s.allowPrimaryLoanClosure : false,
        enableDealer: s.enableDealer !== undefined ? s.enableDealer : false,
        autoFinanceSettlement:
          s.autoFinanceSettlement !== undefined ? s.autoFinanceSettlement : false,
        pgCollectionAmountType:
          s.pgCollectionAmountType !== undefined ? s.pgCollectionAmountType : false,
        isPGEditAmount: s.isPGEditAmount !== undefined ? s.isPGEditAmount : false,
        aggrementNoChecker: s.aggrementNoChecker !== undefined ? s.aggrementNoChecker : false,
        showRSP: s.showRSP !== undefined ? s.showRSP : false,
        isSettlementwithLoanswap:
          s.isSettlementwithLoanswap !== undefined ? s.isSettlementwithLoanswap : false,
        promoterNameInCIBILSubReport:
          s.promoterNameInCIBILSubReport !== undefined ? s.promoterNameInCIBILSubReport : false,
        sendACHSMSforPresentment:
          s.sendACHSMSforPresentment !== undefined ? s.sendACHSMSforPresentment : false,
        isCashDisbursalLimit: s.isCashDisbursalLimit !== undefined ? s.isCashDisbursalLimit : false,
        cashDisbursalAmountLimit:
          s.cashDisbursalAmountLimit !== undefined ? s.cashDisbursalAmountLimit : 20000,
        allowLeadWithSecurityDetails:
          s.allowLeadWithSecurityDetails !== undefined ? s.allowLeadWithSecurityDetails : false,
        isWorkFlowRequiredForFinanceUpload:
          s.isWorkFlowRequiredForFinanceUpload !== undefined
            ? s.isWorkFlowRequiredForFinanceUpload
            : false,
        isSchemeLTV: s.isSchemeLTV !== undefined ? s.isSchemeLTV : false,
        propertyCollateralPage:
          s.propertyCollateralPage !== undefined ? s.propertyCollateralPage : 0,
        isEnableExcessAmtParked:
          s.isEnableExcessAmtParked !== undefined ? s.isEnableExcessAmtParked : false,
        isIgnoreLoanReceiveableAmountCheck:
          s.isIgnoreLoanReceiveableAmountCheck !== undefined
            ? s.isIgnoreLoanReceiveableAmountCheck
            : 0,
        isGenerateFCL: s.isGenerateFCL !== undefined ? s.isGenerateFCL : false,
        fclGenerationValidity:
          s.fclGenerationValidity !== undefined ? s.fclGenerationValidity : null,
        coBorrowerGuarantorLimit:
          s.coBorrowerGuarantorLimit !== undefined ? s.coBorrowerGuarantorLimit : 0,
        nocNumberType: s.nocNumberType !== undefined ? s.nocNumberType : 0
      }
    }
  }
}
</script>
